import React, { useState, useRef, useEffect, forwardRef, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import styles from './GenAiChat.module.scss';
import { getCurrentUser } from 'js/reducers/session';
import { useSelector } from 'react-redux';
import { sendMessage } from './api/streamFetch';
import ChatHeader from './components/ChatHeader';
import ChatInput from './components/ChatInput';
import MessagesContainer from './components/MessagesContainer';
import useResize from './hooks/useResize';
import { ReactComponent as IconResize } from 'new/components/icons/IconResize.svg';
import { GenAITransformInputContext } from 'js/components/recipeBuilder/GenAiTransformButton/GenAITransformContext.types';
import { Message } from './components/MessagesContainer';

import { createMessage } from 'js/components/elements/GenAiChat/utils';

export interface GenAiChatProps {
  style?: React.CSSProperties;
  onStreamComplete?: () => void;
  onClose: () => void;
  zIndex?: number;
  nexsetId: string;
  withNexsetBadge?: boolean;

  noCodeInteraction?: boolean;
  onCodeSelected?: (code: string) => void;

  endpoint?: string;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  selectedCodeTypeOption?: {
    label: string;
    value: string;
    type: string;
  };
  nexsetName?: string;
  selectedNexsetData?: any;
  headerTitle?: string;
  context?: GenAITransformInputContext;
}

const GenAiChat = forwardRef<HTMLDivElement, GenAiChatProps>(
  (
    {
      style,
      onStreamComplete,
      onClose,
      nexsetId,
      withNexsetBadge,
      noCodeInteraction,
      onCodeSelected,
      endpoint = 'genai_transform',
      onMouseDown,
      selectedCodeTypeOption,
      nexsetName,
      selectedNexsetData,
      headerTitle,
      context,
    },
    ref,
  ) => {
    const [messages, setMessages] = useState<Message[]>(() => {
      const savedMessages = localStorage.getItem(`conversation_${nexsetId}`);
      return savedMessages ? JSON.parse(savedMessages) : [];
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const messageIdRef = useRef<number>(1);
    const resizeRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const user = useSelector(getCurrentUser);

    const { handleResizeMouseDown } = useResize(ref as React.RefObject<HTMLElement>);

    useEffect(() => {
      cleanupOldConversations();
    }, []);

    const appendMessage = useCallback(
      (message: Message) => {
        const conversationId = `conversation_${nexsetId}`;
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages, message];
          localStorage.setItem(conversationId, JSON.stringify(updatedMessages));
          return updatedMessages;
        });
      },
      [nexsetId],
    );

    const handleError = (error: string) => {
      setError(error);
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);

      return () => clearTimeout(timer);
    };

    const handleSend = async () => {
      const input = inputRef.current?.value.trim();
      if (!input) return;

      const newMessage: Message = {
        id: messageIdRef.current++,
        text: input,
        isUser: true,
        createdAt: new Date().toISOString(),
      };

      appendMessage(newMessage);
      if (inputRef.current) inputRef.current.value = '';
      setLoading(true);
      setError(null);

      sendMessage({
        input,
        endpoint,
        nexsetId,
        user,
        selectedCodeTypeOption,
        context,
        onSuccess: handleStreamData,
        onError: handleError,
        onComplete: () => {
          setLoading(false);
          if (onStreamComplete) onStreamComplete();
        },
      });
    };

    const handleStreamData = (chunk: string) => {
      try {
        const message = createMessage({ type: endpoint, chunk, handleError });
        if (!message) return;
        message.id = messageIdRef.current++;
        message.context = context ?? [];
        appendMessage(message);
      } catch (e) {
        console.error('Error in handleStreamData:', e);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        handleSend();
      }
    };

    const applyCode = useCallback((code: string) => onCodeSelected(code), [onCodeSelected]);

    const cleanupOldConversations = () => {
      const expirationPeriod = 7 * 24 * 60 * 60 * 1000; // 1 week
      const now = new Date().getTime();

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('conversation_')) {
          const conversation = JSON.parse(localStorage.getItem(key));
          if (conversation.length > 0) {
            const createdAt = new Date(conversation[0].createdAt).getTime();
            if (now - createdAt > expirationPeriod) {
              localStorage.removeItem(key);
            }
          }
        }
      });
    };

    const containerStyle = useMemo(() => style, [style]);

    return (
      <Box
        ref={ref}
        sx={containerStyle}
        className={styles.chatContainer}
        data-testid="genai-chat-container"
      >
        <ChatHeader
          onClose={onClose}
          onMouseDown={onMouseDown}
          withNexsetBadge={withNexsetBadge}
          nexsetName={nexsetName}
          selectedNexsetData={selectedNexsetData}
          headerTitle={headerTitle}
        />

        <MessagesContainer
          messages={messages}
          loading={loading}
          error={error}
          applySuggestions={applyCode}
          noCodeInteraction={noCodeInteraction}
        />

        <ChatInput inputRef={inputRef} handleKeyDown={handleKeyDown} disabled={loading} />

        <Box className={styles.footer}>
          <Box
            ref={resizeRef}
            className={styles.resizeHandle}
            onMouseDown={(e) => {
              handleResizeMouseDown(e);
            }}
          >
            <IconResize />
          </Box>
        </Box>
      </Box>
    );
  },
);

export default GenAiChat;
